import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@mui/material";
import { startCase } from "lodash";
import { formatToLongDate } from "../../../utils/common";
import { API } from "../../../api";
import { StyledWrapper } from "../../dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
} from "../products/style";
import {
  StyledOrdersContainer,
  StyledTableLayout,
} from "../orders/style";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../../virtual-locker/style";
import {
  StyledPaper,
  StyledTableCellBody,
  StyledTableCellHead,
  StyledTableHead,
} from "../../../components/common/table/style";

const cellEllipsisSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && value.trim() === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value;
};

const getSnapshotData = (recordSnapshot) => {
  if (!recordSnapshot) return {};

  if (typeof recordSnapshot === "string") {
    try {
      return JSON.parse(recordSnapshot);
    } catch (error) {
      return {};
    }
  }

  return typeof recordSnapshot === "object" ? recordSnapshot : {};
};

const getAuditRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.result?.data)) return payload.result.data;
  return [];
};

const getAuditTotal = (payload, rows) => {
  if (typeof payload?.total === "number") return payload.total;
  if (typeof payload?.count === "number") return payload.count;
  if (typeof payload?.data?.total === "number") return payload.data.total;
  if (typeof payload?.result?.total === "number") return payload.result.total;
  return rows.length;
};

function mapItemData(data) {
  const recordSnapshot = getSnapshotData(data?.recordSnapshot);
  const user =
    recordSnapshot?.user ||
    data?.user ||
    recordSnapshot?.create_by_user ||
    data?.create_by_user ||
    {};

  const customerName =
    user?.name ||
    recordSnapshot?.name ||
    recordSnapshot?.create_by_user?.name ||
    `${user?.first_name || recordSnapshot?.first_name || ""} ${user?.last_name || recordSnapshot?.last_name || ""}`.trim();

  const rawEntity = data?.entity || recordSnapshot?.entity;
  const entityLabel = rawEntity
    ? startCase(rawEntity) === "Item"
      ? "Product"
      : startCase(rawEntity)
    : "-";

  return {
    id: formatValue(data?.id || recordSnapshot?.id || data?.entityId),
    entity: formatValue(entityLabel),
    email: formatValue(
      user?.email || recordSnapshot?.email || recordSnapshot?.create_by_user?.email
    ),
    customer_name: formatValue(customerName || null),
    phone_number: formatValue(
      user?.phone_number ||
        recordSnapshot?.phone_number ||
        recordSnapshot?.contact_mobile ||
        recordSnapshot?.create_by_user?.phone_number
    ),
    product_name: formatValue(
      recordSnapshot?.product_name || data?.product_name || data?.name
    ),
    actions: formatValue(
      startCase(String(data?.action || recordSnapshot?.action || "").toLowerCase())
    ),
    created_at: formatValue(
      formatToLongDate(data?.createdAt || recordSnapshot?.createdAt || data?.created_at || recordSnapshot?.created_at)
    ),
  };
}

const Audit = () => {
  const navigate = useNavigate();
  const profileDetails = useSelector((state) => state?.auth?.profileDetails);
  const isProfileLoading = useSelector((state) => state?.auth?.isMeLoading);
  const userType = profileDetails?.user_type;
  const isAdmin = userType === "admin";

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isProfileLoading || !userType) return;
    if (!isAdmin) {
      navigate("/login", { replace: true });
    }
  }, [isAdmin, isProfileLoading, navigate, userType]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchAuditLogs = async () => {
      try {
        setIsLoading(true);
        setError("");

        const response = await API.auditLogs({
          search: debouncedSearch,
          page,
          limit: rowsPerPage,
        });

        const payload = response?.data || {};
        const rows = getAuditRows(payload);
        setLogs(rows);
        setTotal(getAuditTotal(payload, rows));
      } catch (apiError) {
        setError(apiError?.data?.message || "Failed to load audit logs.");
        setLogs([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditLogs();
  }, [debouncedSearch, isAdmin, page, rowsPerPage]);

  if (isProfileLoading || !userType) {
    return null;
  }

  return (
    <StyledWrapper>
      <StyledTableLayout>
        <StyledTypographyText>AUDIT LOGS</StyledTypographyText>
        <StyledBoxStyle>
          <StyledCustomerSearch
            placeholder="Search by Email and Phone Number"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </StyledBoxStyle>
        <StyledOrdersContainer>
          <StyledPaper>
            <TableContainer
              sx={{
                width: "100%",
                overflowX: "auto",
              }}
            >
              <Table
                sx={{
                  minWidth: 1400,
                  tableLayout: "auto",
                }}
              >
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCellHead sx={{ minWidth: 100 }}>No.</StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 220 }}>
                      PRODUCT NAME
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 140 }}>ENTITY</StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 120, maxWidth: 120 }}>
                      EVENT
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 220 }}>EMAIL</StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 180 }}>
                      CUSTOMER NAME
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 170 }}>PHONE NUMBER</StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 180 }}>
                      EVENT DATE
                    </StyledTableCellHead>
                  </TableRow>
                </StyledTableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box py={4}>
                          <CircularProgress size={28} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : logs.length ? (
                    logs.map((log, index) => {
                      const mappedItem = mapItemData(log);
                      const rowNumber = (page - 1) * rowsPerPage + index + 1;

                      return (
                        <TableRow key={log?.id || index}>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 100,
                              maxWidth: 100,
                              ...cellEllipsisSx,
                            }}
                          >
                            {rowNumber}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 220,
                              maxWidth: 220,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.product_name}
                          >
                            {mappedItem.product_name}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 140,
                              maxWidth: 140,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.entity}
                          >
                            {mappedItem.entity}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 120,
                              maxWidth: 120,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.actions}
                          >
                            {mappedItem.actions}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 220,
                              maxWidth: 220,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.email}
                          >
                            {mappedItem.email}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 180,
                              maxWidth: 180,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.customer_name}
                          >
                            {mappedItem.customer_name}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 170,
                              maxWidth: 170,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.phone_number}
                          >
                            {mappedItem.phone_number}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            sx={{
                              minWidth: 180,
                              maxWidth: 180,
                              ...cellEllipsisSx,
                            }}
                            title={mappedItem.created_at}
                          >
                            {mappedItem.created_at}
                          </StyledTableCellBody>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No audit logs found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total || 0}
              page={page - 1}
              onPageChange={(event, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </StyledPaper>
        </StyledOrdersContainer>
      </StyledTableLayout>
    </StyledWrapper>
  );
};

export default Audit;
